import type { FrequencyPeriod } from 'api/recoveryAi.types'
import ChevronDown from 'components/Icons/ChevronDown'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { FormControl } from 'react-bootstrap'
import { useStyletron } from 'styletron-react'
import { FREQUENCY_PERIODS } from './periodFormat'

const t = buildTranslate('recovery_ai.wizard.limits')

const CHEVRON_OFFSET = '8px'

type Props = {
  value: FrequencyPeriod
  onChange: (period: FrequencyPeriod) => void
  ariaLabel: string
  disabled?: boolean
}

// Native <select> styled as a boxed dropdown (AI-902). A design-system icon
// stands in for the arrow; `backgroundImage: none` below is required to
// suppress the global `_select.scss` PNG arrow (`appearance: none` doesn't
// touch background-image — the global rule wins on specificity otherwise).
const InlinePeriodSelect = ({
  value,
  onChange,
  ariaLabel,
  disabled,
}: Props) => {
  const [css] = useStyletron()

  const chevronColor = disabled ? palette.grey40 : palette.grey70

  const selectStyle = css({
    width: 'auto',
    display: 'inline-block',
    border: `1px solid ${palette.grey40}`,
    borderRadius: '4px',
    backgroundColor: disabled ? palette.grey10 : palette.white,
    color: disabled ? palette.grey40 : palette.grey90,
    fontWeight: 600,
    fontSize: '14px',
    padding: '4px 24px 4px 8px',
    cursor: disabled ? 'default' : 'pointer',
    appearance: 'none',
    backgroundImage: 'none',
    boxShadow: 'none',
  })

  const wrapperStyle = css({
    position: 'relative',
    display: 'inline-block',
  })

  const chevronStyle = css({
    position: 'absolute',
    top: '50%',
    right: CHEVRON_OFFSET,
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  })

  return (
    <div className={wrapperStyle}>
      <FormControl
        disabled={disabled}
        componentClass="select"
        value={value}
        onChange={(e: React.FormEvent<FormControl>) =>
          onChange((e.target as HTMLSelectElement).value as FrequencyPeriod)
        }
        aria-label={ariaLabel}
        className={selectStyle}
      >
        {FREQUENCY_PERIODS.map(period => (
          <option key={period} value={period}>
            {t(`period_${period}`)}
          </option>
        ))}
      </FormControl>
      <span aria-hidden="true" className={chevronStyle}>
        <ChevronDown fillColor={chevronColor} />
      </span>
    </div>
  )
}

export default InlinePeriodSelect
